/**
 * Activity log — auditoria.
 */
import { useQuery } from "@tanstack/react-query";
import { Activity, Briefcase, Building2, FileText, HardHat, Receipt, Tag, User as UserIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonRow } from "@/components/ui/skeleton";
import { activityApi } from "@/lib/api-system";
import { formatDateTime, formatRelative } from "@/lib/format";

const ENTITY_ICONS: Record<string, LucideIcon> = {
  client: Building2,
  nerd: HardHat,
  job: Briefcase,
  contract: FileText,
  category: Tag,
  invoice: Receipt,
  user: UserIcon,
};

export function ActivityPage() {
  const list = useQuery({ queryKey: ["activity"], queryFn: () => activityApi.list({ size: 200 }) });

  return (
    <>
      <PageHeader
        title="Histórico de atividades"
        description="Auditoria — quem fez o quê, quando."
      />

      {list.isLoading ? (
        <div className="space-y-2">
          <SkeletonRow cols={4} /><SkeletonRow cols={4} /><SkeletonRow cols={4} />
        </div>
      ) : list.data?.items.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Sem atividades registradas"
          description="O histórico aparecerá conforme a equipe interage com o sistema."
        />
      ) : (
        <ol className="relative space-y-3">
          {list.data?.items.map((entry) => {
            const Icon = entry.entity_type ? ENTITY_ICONS[entry.entity_type] ?? Activity : Activity;
            return (
              <li key={entry.id} className="flex items-start gap-3 rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
                <Avatar size="sm">
                  <AvatarFallback name={entry.actor_email ?? "?"} />
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{entry.actor_email ?? "Sistema"}</span>
                    <Badge variant="default" className="font-mono text-[10px]">{entry.action}</Badge>
                    {entry.entity_type && (
                      <Badge variant="muted" size="sm">
                        <Icon className="h-2.5 w-2.5" /> {entry.entity_type}
                      </Badge>
                    )}
                  </div>
                  {entry.summary && <p className="text-sm text-foreground mt-1">{entry.summary}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1" title={formatDateTime(entry.created_at)}>
                    {formatRelative(entry.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
}
